import { ActionTree } from "vuex";
import { Recipes } from "@/types/recipes";
import {
  SearchState,
  RootState,
  RequestSearchKeyword,
  RequestSearchKeywordTags,
  TagGroups,
  ApplianceData,
  TagGroupInfo,
} from "@/types/search";
import store from "@/store";
import apiClient from "@/client/api-client";
import { API_URL_DEVICE, API_URL_RECIPE } from "@/const/api-const";
import { getApplianceListText } from "@/utils/search-util";
import { Product } from "@/types/common";
import NativeClient from "@/client/native-client";

const actions: ActionTree<SearchState, RootState> = {
  updateSearchKeywordRecipes: async ({ commit }, recipes: Recipes | null) => {
    commit("updateSearchKeywordRecipes", recipes);
  },
  searchKeywordRecipesAndTags: async ({ commit }, payload: RequestSearchKeyword) => {
    const tags: TagGroups | null = store.getters["search/getTagGroups"];
    const payload2: RequestSearchKeywordTags = {
      keyword: payload.keyword,
      sort: payload.sort,
      tags: payload.tags,
      tagGroups: tags,
      appliance: payload.appliance
    };
    // すでに全件取得済みの場合はなにもしない
    if (!(tags && tags.tagGroups && tags.tagGroups.length >= 1)) {
      // タグ検索API呼び出し
      const resTag = await apiClient.get(API_URL_RECIPE.TAGS);
      commit("updateTagGroups", resTag.data);
      payload2.tagGroups = resTag.data;
    }
    const recipes: Recipes | null =
      store.getters["search/getSearchKeywordRecipes"];
    if (recipes && recipes.recipes) {
      if (recipes.totalCount <= recipes.recipes.length) {
        return;
      }
    }
    // すでに取得済みの場合、取得した件数以降の開始位置を指定
    const start = recipes && recipes.recipes ? recipes.recipes.length : 0;
    let tagsText = "";
    if (payload2.tagGroups != null && payload2.tags != null) {
      payload2.tagGroups.tagGroups.forEach(tagGroups => {
        let tags = "";
        payload2.tags.forEach(tag => {
          if (tagGroups.alias == tag.alias) {
            if (tags != "") {
              tags += "|";
            }
            tags += tag.id;
          }
        });
        if (tags != "") {
          if (tagsText != "") {
            tagsText += " ";
          }
          if (tagGroups.operation) {
            tagsText += tagGroups.operation + "(" + tags + ")";
          } else {
            tagsText += "OR" + "(" + tags + ")";
          }
        }
      });
    }
    NativeClient.sendDebugLog("searchKeywordRecipesAndTags tagsText=" + tagsText);
    NativeClient.sendDebugLog("searchKeywordRecipesAndTags payload2.appliance=" + payload2.appliance);

    // レシピ検索API呼び出し
    const res = await apiClient.get(API_URL_RECIPE.SEARCH, {
      params: {
        // My家電ID
        myAppliance: payload2.appliance != "" ? payload2.appliance : null,
        // キーワード
        q: payload2.keyword,
        // 取得開始位置
        start: start,
        // 取得件数は 10 件固定
        row: 10,
        // 検索結果のソート順：一致
        order: payload2.sort,
        // タグ
        tags: tagsText != "" ? tagsText : null,
        // 品番指定検索の品番
        productId: payload2.appliance != "" ? payload2.appliance : null,
      }
    });
    commit("updateSearchKeywordRecipes", res.data);
  },
  getRecommendRecipes: async ({ commit }) => {
    // Nativeから受け取った認証関連パラメータを取得
    const apiParams = store.getters["common/getApiBaseParams"];
    const recipes: Recipes | null = store.getters["search/getRecommendRecipes"];
    // すでに全件取得済みの場合はなにもしない
    if (recipes && recipes.recipes) {
      if (recipes.totalCount <= recipes.recipes.length) {
        return;
      }
    }

    const applianceText = getApplianceListText(apiParams.applianceList);
    // レシピ検索API呼び出し
    const res = await apiClient.get(API_URL_RECIPE.RECOMMEND, {
      params: {
        // My家電ID
        myAppliance: (applianceText != "") ? applianceText : null,
        // 取得開始位置（すでに取得済みの場合、取得した件数以降の開始位置を指定）
        start: 0,
        // 取得件数は 10 件固定
        row: 10
      }
    });

    commit("updateRecommendRecipes", res.data);
  },
  getRankingRecipes: async ({ commit }) => {
    // Nativeから受け取った認証関連パラメータを取得
    const apiParams = store.getters["common/getApiBaseParams"];
    const recipes: Recipes | null = store.getters["search/getRankingRecipes"];

    // すでに全件取得済みの場合はなにもしない
    if (recipes && recipes.recipes) {
      if (recipes.totalCount <= recipes.recipes.length) {
        return;
      }
    }

    // レシピ検索API呼び出し
    const applianceText = getApplianceListText(apiParams.applianceList);
    const res = await apiClient.get(API_URL_RECIPE.RANKING, {
      params: {
        // My家電ID
        myAppliance: (applianceText != "") ? applianceText : null,
        // 取得開始位置（すでに取得済みの場合、取得した件数以降の開始位置を指定）
        start: 0,
        // 取得件数は 10 件固定
        row: 10
      }
    });

    commit("updateRankingRecipes", res.data);
  },
  getTagGroups: async ({ commit }) => {
    const tags: TagGroups | null = store.getters["search/getTagGroups"];

    // すでに全件取得済みの場合はなにもしない
    if (tags && tags.tagGroups && tags.tagGroups.length >= 1) {
      return;
    }

    // タグ検索API呼び出し
    const res = await apiClient.get(API_URL_RECIPE.TAGS);
    commit("updateTagGroups", res.data);
  },
  updateTagGroups: async ({ commit }, tags: TagGroups | null) => {
    commit("updateTagGroups", tags);
  },
  updateKeyword: async ({ commit }, keyword: string | null) => {
    commit("updateKeyword", keyword);
  },
  updateSortType: async ({ commit }, sort: string | null) => {
    commit("updateSortType", sort);
  },
  updateTags: async ({ commit }, tags: string | null) => {
    commit("updateTags", tags);
  },
  updateProducts: async ({ commit }, products: Array<Product> | null) => {
    commit("updateProducts", products);
  },
  getProducts: async ({ commit }) => {
    const products: Array<Product> | null = store.getters["search/getProducts"];
    // すでに全件取得済みの場合はなにもしない
    if (products) {
      return;
    }

    // レシピ検索API呼び出し
    const res = await apiClient.get(API_URL_DEVICE.REGISTERED, {
      params: {
        // IoT対象
        isIotDevice: null
      }
    });
    commit("updateProducts", res.data);
  },
  updateApplianceList: async ({ commit }, products: Array<ApplianceData> | null) => {
    commit("updateApplianceList", products);
  },
};

export default actions;
